import {
	Box,
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	Fade,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useStore } from "../../../store/store";
import { useRelatedRecords } from "../../../api/useRelatedRecords";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { Wysiwyg } from "../../../components/wysiwyg/Wysiwyg";
import { ClientAndRelativesEmailSelectionField } from "../../../components/fieldsInputs/fields/customized/ClientAndRelativesEmailSelectionField";
import { api } from "../../../api/config";
import axios from "axios";
import { MAKE_WEBHOOKS } from "../../../utils/constants";
import { PdfDocumentDownloadButton } from "../../../components/pdf/PdfDownloadButton";

const OTHER_NAMES_FOR_DOCUMENT = "Outro";

type PresenceDeclarationDocumentDialogProps = {
	context: any;
	onClose: () => void;
};

export const PresenceDeclarationDocumentDialog = ({ context, onClose }: PresenceDeclarationDocumentDialogProps) => {
	const { notifications } = useStore();
	const [isLoading, setIsLoading] = useState(false);
	const [downloadUrl, setDownloadUrl] = useState<string>("");
	const [pdfGenerationError, setPdfGenerationError] = useState(false);

	const client = context?.item?.treatment?.client;

	const { data: relatives } = useRelatedRecords("relatives", "Nome,email", "client_id", client?.id);
	
	const relativesData =
		relatives?.items?.map((relative: { Nome: string; email: string }) => ({
			name: relative?.Nome || "",
			email: relative?.email || "",
		})) || [];

	const clientData = {
		name: client?.user_id?.first_name + " " + client?.user_id?.last_name || "",
		email: client?.user_id?.email || "",
	};

	const leadData = {
		name: context?.item?.lead?.first_name + " " + context?.item?.lead?.last_name || "",
		email: context?.item?.lead?.email || "",
	};

	const effectiveClientData = client ? clientData : leadData;

	const possibleNamesForDocument = [
		effectiveClientData ? { name: effectiveClientData.name, type: "Cliente" } : null,
		...relativesData.map((relative: { name: string }) =>
			relative.name ? { name: relative.name, type: "Parente" } : null
		),
		{ name: OTHER_NAMES_FOR_DOCUMENT, type: OTHER_NAMES_FOR_DOCUMENT },
	].filter((item) => item !== null) as Array<{ name: string; type: string }>;

	const emailDescription = `Olá,
	<br><br>
	Segue em anexo a declaraçao de presença requisitada.<br>
	Algum outro assunto, encontramo-nos à sua disposição.
	<br><br>
	Obrigado,<br>
	NeuroImprove Clinic.`;

	const entityForm = useForm({
		defaultValues: {
			content: emailDescription,
			attachment: null as unknown as Blob,
			nameForDocument: possibleNamesForDocument[0]?.name ?? "",
			customNameForDocument: "",
			emailData: {
				id: `${effectiveClientData.email}-${effectiveClientData.name}` || "",
				name: effectiveClientData.name.split(" ")[0] || "",
				email: effectiveClientData.email || "",
			},
		},
	});
	const watchForm = entityForm.watch();

	const selectedNameForDocument =
		watchForm.nameForDocument === OTHER_NAMES_FOR_DOCUMENT
			? watchForm.customNameForDocument
			: watchForm.nameForDocument;

	useEffect(() => {
		return () => {
			if (downloadUrl) URL.revokeObjectURL(downloadUrl);
		};
	}, [downloadUrl]);

	const handleGenerateDocument = async () => {
		setIsLoading(true);
		try {
			const requestData = {
				document_name: selectedNameForDocument,
				client_name: effectiveClientData.name,
				clinic_id: context?.item?.clinic_id?.id,
				session: { start_date: context?.item?.start_date, end_date: context?.item?.end_date, status: context?.item?.status },
			};

			const response = await api.post("generate-pdf/presence-declaration", requestData, {
				responseType: "blob",
			});
  
			const pdfBlob = new Blob([response.data], { type: "application/pdf" });
			entityForm.setValue("attachment", pdfBlob);

			const url = URL.createObjectURL(pdfBlob);
			setDownloadUrl(url);

			notifications.setNotification({
				isOpen: true,
				message: "Documento gerado com sucesso",
				severity: "success",
			});
		} catch (error: any) {
			setPdfGenerationError(true);
			let errorMessage = "Erro ao gerar documento";

			if (error?.response?.data) {
				try {
					const blobData = error.response.data;
					errorMessage = await blobData.text();

				} catch(error: any) {
					console.error("erro na vaidaçao de pdf",error)
					errorMessage = "Erro ao gerar documento, relaciondado a validação"
				}
			}

			notifications.setNotification({
				isOpen: true,
				message: `Error - ${errorMessage}`,
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSendEmail = async (formData: {
		content: string;
		emailData: { id: string; name: string; email: string };
		attachment: Blob;
	}) => {
		notifications.setNotification({
			isOpen: false,
		});
		setIsLoading(true);

		try {
			const form = new FormData();
			form.append("name", formData.emailData.name);
			form.append("email", formData.emailData.email.trim());
			form.append("subject", "Declaração de Presença - NeuroImprove Clinic");
			form.append("content", formData.content);
			form.append("attachment", formData.attachment, `declaracao_presenca_${effectiveClientData.name}.pdf`);

			const response = await axios({
				method: "POST",
				url: MAKE_WEBHOOKS.send_email_with_optional_attachment,
				data: form,
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			const isSuccess = response.status === 200;
			notifications.setNotification({
				isOpen: true,
				message: response.data,
				severity: isSuccess ? "success" : "error",
			});

			entityForm.reset();
			onClose();
		} catch (error: any) {
			notifications.setNotification({
				isOpen: true,
				message: error?.response?.data?.errorMessage || "Erro ao enviar email",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const documentName = `declaracao_presenca_${effectiveClientData.name}`;
	const documentButtonText = `Declaração de Presença - ${effectiveClientData.name}`;

	return (
		<form onSubmit={entityForm.handleSubmit(handleSendEmail)}>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: "600px" }}>
				<GenerateDocument
					entityForm={entityForm as unknown as UseFormReturn}
					possibleNames={possibleNamesForDocument}
					disabled={!!downloadUrl}
				/>
				<PdfDocumentDownloadButton
					downloadUrl={downloadUrl}
					buttonText={documentButtonText}
					fileName={documentName}
					pdfGenerationError={pdfGenerationError}
					isLoading={isLoading}
				/>
				<Fade in={!!downloadUrl} timeout={500}>
					<Box>
						{downloadUrl && (
							<SendDocumentEmail
								entityForm={entityForm as unknown as UseFormReturn}
								clientData={effectiveClientData}
								relativesData={relativesData}
							/>
						)}
					</Box>
				</Fade>
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose}>
					Cancelar
				</Button>
				{downloadUrl ? (
					<Button type="submit" disabled={downloadUrl === ""}>
						{isLoading ? <CircularProgress color="inherit" size={24} /> : "Enviar"}
					</Button>
				) : (
					<Button type="button" onClick={handleGenerateDocument} disabled={isLoading}>
						{isLoading ? <CircularProgress color="inherit" size={24} /> : "Gerar documento"}
					</Button>
				)}
			</DialogActions>
		</form>
	);
};

const GenerateDocument = ({
	entityForm,
	possibleNames,
	disabled,
}: {
	entityForm: UseFormReturn;
	possibleNames: Array<{ name: string; type: string }>;
	disabled: boolean;
}) => {
	const selectedName = entityForm.watch("nameForDocument");
	const presentType = (type: string) => {
		if (type === OTHER_NAMES_FOR_DOCUMENT) return "";
		return `(${type})`;
	};

	return (
		<Box sx={{ mb: 4 }}>
			<Controller
				name="nameForDocument"
				control={entityForm.control}
				render={({ field }) => {
					return (
						<FormControl fullWidth>
							<InputLabel>Selecione o nome para o documento</InputLabel>
							<Select
								{...field}
								label="Selecione o nome para o documento"
								value={field.value}
								onChange={(e: any) => field.onChange(e.target.value)}
								sx={{ color: field.value === OTHER_NAMES_FOR_DOCUMENT ? "text.disabled" : "inherit" }}
								disabled={disabled}
							>
								{possibleNames.map((item) => (
									<MenuItem key={item.name} value={item.name}>
										{item.name} {presentType(item.type)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					);
				}}
			/>
			{selectedName === OTHER_NAMES_FOR_DOCUMENT && (
				<Controller
					name="customNameForDocument"
					control={entityForm.control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							label="Escreva o nome"
							placeholder="Insira um nome personalizado"
							sx={{ mt: 4 }}
							disabled={disabled}
						/>
					)}
				/>
			)}
		</Box>
	);
};

const SendDocumentEmail = ({
	entityForm,
	clientData,
	relativesData,
}: {
	entityForm: UseFormReturn;
	clientData: { name: string; email: string };
	relativesData: Array<{ name: string; email: string }>;
}) => {
	const emailDescription = entityForm.getValues("content");
	return (
		<Box>
			<Typography variant="body1" sx={{ fontWeight: 600, my: 3, color: "primary.main" }}>
				Enviar Declaração de Presença por email
			</Typography>
			<Box height="270px" overflow="auto">
				<Wysiwyg
					initialValue={emailDescription}
					isDisabled={true}
					canMention={false}
					entityForm={entityForm as unknown as UseFormReturn}
				/>
			</Box>

			<ClientAndRelativesEmailSelectionField
				entityForm={entityForm as unknown as UseFormReturn}
				client={clientData}
				relatives={relativesData}
			/>
		</Box>
	);
};


